<template>
<div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">

  <!-- Sidebar -->
  <HrSidebar :isOpen="isSidebarOpen" />

  <!-- Main -->
  <main 
    class="flex-1 overflow-x-hidden transition-all duration-300"
    :class="{'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen}"
  >

  <!-- Navbar -->
  <header class="fixed top-0 z-50 w-full bg-white dark:bg-gray-800 shadow-sm">
    <HrTopbar
      :page-title="currentPageTitle"
      :is-sidebar-open="isSidebarOpen"
      :is-dark-mode="isDarkMode"
      @toggle-sidebar="toggleSidebar"
      @toggle-dark-mode="toggleDarkMode"
    />
  </header>

  <!-- Content -->
  <div class="p-6 mt-16">

    <!-- Top Bar -->
    <div class="flex justify-between items-center mb-8 p-4">

      <input
        v-model="search"
        type="text"
        placeholder="Search position..."
        class="border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 w-72 focus:ring focus:ring-blue-300 dark:bg-gray-700 dark:text-white"
      />

      <button
        @click="openCreate"
        class="bg-[#74bd54] text-white px-5 py-2 rounded-lg hover:bg-[#63a43f] shadow"
      >
        + Add Position
      </button>

    </div>

    <!-- Positions Grid -->
    <div
      v-if="filteredPositions.length"
      class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6"
    >

      <div
        v-for="pos in filteredPositions"
        :key="pos.id"
        class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow hover:shadow-xl transition"
      >

        <!-- Header -->
        <div class="flex justify-between mb-4">

          <div class="flex items-center space-x-3">

            <div class="w-12 h-12 rounded-full bg-secondary text-white flex items-center justify-center font-bold">
              {{ pos.code }}
            </div>

            <div>
              <h3 class="font-semibold text-gray-800 dark:text-white">
                {{ pos.name }}
              </h3>

              <span class="text-xs text-gray-500">
                {{ pos.staff }} staff
              </span>
            </div>

          </div>

        </div>

        <!-- Department Badge -->
        <span class="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
          {{ pos.department_name }}
        </span>

        <!-- Description -->
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-3 mb-6">
          {{ pos.description }}
        </p>

        <!-- Actions -->
        <div class="flex justify-between text-sm">

          <button
            @click="editPosition(pos)"
            class="text-blue-600 hover:underline"
          >
            Edit
          </button>

          <button
            @click="deletePosition(pos.id)"
            class="text-red-500 hover:underline"
          >
            Delete
          </button>

        </div>

      </div>

    </div>

    <!-- Empty State -->
    <div
      v-else
      class="text-center text-gray-400 mt-20"
    >
      No positions created yet
    </div>


    <!-- Modal -->
    <Transition name="modal">
    <div
      v-if="showModal"
      class="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
    >

      <div class="bg-white dark:bg-gray-800 rounded-xl p-6 w-[420px] shadow-lg">

        <h3 class="text-lg font-semibold text-[#035a76] mb-6">
          {{ editing ? 'Edit Position' : 'Create Position' }}
        </h3>

        <form @submit.prevent="savePosition" class="space-y-4">

          <input
            v-model="form.name"
            type="text"
            placeholder="Position Name"
            required
            class="w-full border border-gray-300 dark:border-gray-600 rounded p-2 dark:bg-gray-700 dark:text-white"
          />

          <select
            v-model="form.department_id"
            required
            placeholder="Select Department"
            class="w-full border border-gray-300 dark:border-gray-600 rounded p-2 dark:bg-gray-700 dark:text-white"
          >
            <option disabled value="">Select Department</option>
            <option v-for="dept in departments" :value="dept.id" :key="dept.id">
              {{ dept.name }}
            </option>
          </select>

          <input
            v-model="form.code"
            type="text"
            placeholder="Position Code"
            required
            class="w-full border border-gray-300 dark:border-gray-600 rounded p-2 dark:bg-gray-700 dark:text-white"
          />

          <!-- <input
            v-model="form.staff"
            type="number"
            placeholder="Staff count"
            class="w-full border border-gray-300 dark:border-gray-600 rounded p-2 dark:bg-gray-700 dark:text-white"
          /> -->

          <textarea
            v-model="form.description"
            placeholder="Description"
            class="w-full border border-gray-300 dark:border-gray-600 rounded p-2 dark:bg-gray-700 dark:text-white"
          ></textarea>

          <div class="flex justify-end space-x-3 pt-2">

            <button
              type="button"
              @click="closeModal"
              class="px-4 py-2 bg-gray-300 rounded"
            >
              Cancel
            </button>

            <button
              type="submit"
              class="px-4 py-2 bg-[#74bd54] text-white rounded hover:bg-[#63a43f]"
            >
              Save
            </button>

          </div>

        </form>

      </div>

    </div>
    </Transition>

  </div>

  </main>
</div>
</template>

<script setup>

import { ref, computed, onMounted } from "vue"
import api from "@/services/api"
import Swal from "sweetalert2";
import HrSidebar from "../../components/hr/HrSidebar.vue"
import HrTopbar from "../../components/hr/HrTopbar.vue"

const currentPageTitle = ref("Positions")

const isSidebarOpen = ref(true)
const isDarkMode = ref(false)

const showModal = ref(false)
const editing = ref(false)

const search = ref("")

// const departments = [
// "Human Resources",
// "Finance",
// "IT",
// "Operations"
// ]

const departments = ref([])

const positions = ref([])

// const positions = ref([
// {
// id:1,
// name:"HR Manager",
// code:"HRM",
// department:"Human Resources",
// description:"Oversees recruitment and HR policies",
// staff:1
// },
// {
// id:2,
// name:"Accountant",
// code:"ACC",
// department:"Finance",
// description:"Manages financial records",
// staff:3
// }
// ])

const form = ref({
id:null,
name:"",
department:"",
code:"",
description:"",
department_id:"",
staff:0
})

const filteredPositions = computed(() => {
return positions.value.filter(p =>
(p.name || "").toLowerCase().includes(search.value.toLowerCase())
)
})

const openCreate = () => {
editing.value=false
form.value={id:null,name:"",department:"",code:"",description:"", description_id:"", staff:0}
showModal.value=true
}

const editPosition = (pos) => {
editing.value=true
form.value={...pos}
showModal.value=true
}

const fetchDepartments = async () => {
  try{
    const response = await api.get("Departments.php");
    departments.value = response.data.map(department => ({
      id:department.id,
      name:department.name,
      code:department.code,
      description:department.description
    }));

  } catch (error) {
    console.error("Error fetching Departments:", error);
  }
};

const fetchPositions = async () => {
  try {
    const response = await api.get("Positions.php");

    positions.value = response.data.map(position => ({
      id: position.id,
      department_id: position.department_id,
      department_name : position.department_name,
      name: position.title,
      code: position.code,
      description: position.description
    }));

  } catch (error) {
    console.error("Error fetching Positions:", error);
  }
};

// const savePosition = () => {

// if(editing.value){

// const index=positions.value.findIndex(p=>p.id===form.value.id)
// positions.value[index]={...form.value}

// }else{

// positions.value.push({
// id:Date.now(),
// ...form.value
// })

// }

// closeModal()

// }

const savePosition = async () => {
  try {
    let response;

    if (editing.value) {
      response = await api.put("Positions.php", form.value);

      const index = positions.value.findIndex(p => p.id === form.value.id);
      if (index !== -1) {
        positions.value[index] = { ...form.value };
      }

    } else {
      response = await api.post("Positions.php", form.value);

      positions.value.push({
        id: response.data.id,
        title: response.data.title,
        code: response.data.code,
        description: response.data.description,
        department_id: response.data.department_id
      });
    }

    closeModal();

  } catch (error) {
    console.error("Error saving position:", error);
  }
};




// const deletePosition=(id)=>{
// positions.value=positions.value.filter(p=>p.id!==id)
// }

const deletePosition = async (id) => {
  try {

    await api.delete("Positions.php", {
      data: { id: id }
    });

    positions.value = positions.value.filter(u => u.id !== id);

  } catch (error) {
    console.error("Error deleting position:", error);
  }
};

const closeModal=()=>{
showModal.value=false
}

const toggleSidebar=()=>{
isSidebarOpen.value=!isSidebarOpen.value
}

const toggleDarkMode=()=>{
isDarkMode.value=!isDarkMode.value

if(isDarkMode.value){
document.documentElement.classList.add("dark")
}else{
document.documentElement.classList.remove("dark")
}

}
onMounted (() =>{
  fetchDepartments();
  fetchPositions();
})

</script>

<style>

.modal-enter-active,
.modal-leave-active{
transition:all .25s ease;
}

.modal-enter-from{
opacity:0;
transform:scale(.9);
}

.modal-leave-to{
opacity:0;
transform:scale(.9);
}

</style>